/**
 * The app's project file kept byte for byte, the one owner of its bytes: what no edit touched is copied as read, and
 * nothing here reads the scene IR, so no rounding, clamp or catalog size of the parse can reach the file. The web's own
 * edits (`setPosition`, `setRotation`, `insert`) keep to the names the app writes; what the app relays (`insertFromApp`,
 * `setNumber`, `setText`, `replaceChild`, `move`) lands as the app wrote it.
 */

/** An element's address in the file as read: [tag, index among its same-tag siblings] steps from `<Project>`. */
export type ElementPath = ReadonlyArray<readonly [tag: string, index: number]>;

export type ProjectFileErrorCode = 'not-a-project' | 'malformed' | 'not-found' | 'not-finite' | 'unknown-name' | 'missing-id'
  | 'duplicate-id' | 'removed' | 'unsupported';

export class ProjectFileError extends Error {
  constructor(readonly code: ProjectFileErrorCode, message: string = code) {
    super(message);
    this.name = 'ProjectFileError';
  }
}

export interface ProjectElement {
  readonly tag: string;
  /** The attribute's value as the app reads it (entities decoded), as the edits so far left it. */
  attribute(name: string): string | undefined;
}

/** The tags the app writes a `UniqueId` on (Building, Level, Room, Floor, Ceiling, WallSide and Furniture with its kin). */
export const ID_TAGS: ReadonlySet<string> = new Set(['Building', 'Level', 'Room', 'Floor', 'Ceiling', 'Wall', 'Opening', 'Furniture',
  'Plinth', 'CeiliningMolding', 'Front', 'Back', 'Left', 'Right']);

/**
 * The names an edit may add beyond those the file has: only what the app writes itself. Its multiplayer drops every
 * app client from the session on a name the app's own Open→Save loses (`VersionProjectController.InspectRoundTrip`).
 */
export const APP_WRITES: ReadonlySet<string> = new Set(['Position', 'Position@X', 'Position@Y', 'Furniture@Rotation', 'Opening@Rotation',
  'Wall@Rotation', ...[...ID_TAGS].map(tag => `${tag}@UniqueId`)]);

/** The app's `Furniture` and its kin: the elements with a `<Position X Y>` and a `Rotation` of their own. */
const PLACED: ReadonlySet<string> = new Set(['Furniture', 'Opening', 'Wall']);

const WHOLE = Math.fround(0.00005);

/** `SerializationHelper.SerializeFloat`: a float32 less than 0.00005 above a whole as that whole (`F0`), else `R` — G7 when it reads back as the same float, else G9. */
export function formatFloat(value: number): string {
  const f = Math.fround(value);
  if (!Number.isFinite(f)) throw new ProjectFileError('not-finite', `${value} is not a number the app can hold`);
  if (Math.abs(f) % 1 < WHOLE) return String(Math.trunc(f) || 0);
  const short = general(f, 7);
  return Math.fround(Number(short)) === f ? short : general(f, 9);
}

/** .NET's `G<precision>`: fixed while the exponent is above −5 and below the precision, else `d.dddE±dd`; no trailing zeros. */
function general(f: number, precision: number): string {
  const [mantissa, power] = f.toExponential(precision - 1).split('e');
  const exponent = Number(power), sign = f < 0 ? '-' : '';
  const digits = mantissa.replace(/[-.]/g, '').replace(/0+$/, '') || '0';
  if (exponent <= -5 || exponent >= precision) {
    return `${sign}${digits[0]}${digits.length > 1 ? `.${digits.slice(1)}` : ''}E${exponent < 0 ? '-' : '+'}${String(Math.abs(exponent)).padStart(2, '0')}`;
  }
  if (exponent < 0) return `${sign}0.${'0'.repeat(-exponent - 1)}${digits}`;
  const fraction = digits.slice(exponent + 1);
  return `${sign}${digits.slice(0, exponent + 1).padEnd(exponent + 1, '0')}${fraction ? `.${fraction}` : ''}`;
}

const ENTITY = /&(?:#x([0-9a-fA-F]+)|#([0-9]+)|(amp|lt|gt|quot|apos));/g;
const REFERENCE = /&(?:#x[0-9a-fA-F]+|#[0-9]+|amp|lt|gt|quot|apos);/y;
const NAMED: Readonly<Record<string, string>> = { amp: '&', lt: '<', gt: '>', quot: '"', apos: '\'' };
/** Outside XML 1.0's `Char`: what .NET's reader and writer refuse (control characters, a lone surrogate, U+FFFE/U+FFFF). */
const FORBIDDEN = /[\u0000-\u0008\u000B\u000C\u000E-\u001F\uFFFE\uFFFF]|[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?<![\uD800-\uDBFF])[\uDC00-\uDFFF]/;
const ESCAPES: Readonly<Record<string, string>> = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', '\n': '&#xA;', '\r': '&#xD;', '\t': '&#x9;' };

const isXmlChar = (code: number) => code === 0x9 || code === 0xA || code === 0xD || (code >= 0x20 && code <= 0xD7FF)
  || (code >= 0xE000 && code <= 0xFFFD) || (code >= 0x10000 && code <= 0x10FFFF);

/** An attribute value as .NET's `XmlWriter` writes it between double quotes. */
export const escapeAttribute = (value: string) => value.replace(/[&<>"\n\r\t]/g, char => ESCAPES[char]);

function decode(raw: string): string {
  if (!raw.includes('&')) return raw;
  return raw.replace(ENTITY, (match, hex: string | undefined, dec: string | undefined, name: string | undefined) => {
    if (name) return NAMED[name];
    const code = parseInt(hex ?? dec!, hex ? 16 : 10);
    return code <= 0x10ffff ? String.fromCodePoint(code) : match;
  });
}

interface Attribute { name: string; lead: number; valueStart: number; valueEnd: number }

class Element implements ProjectElement {
  readonly children: Element[] = [];
  readonly attributes: Attribute[] = [];
  nameEnd = 0;
  attributesEnd = 0;
  startTagEnd = 0;
  /** −1 for a self-closing element. */
  endTagStart = -1;
  end = 0;
  removed = false;
  dirty = false;
  /** Attribute name → its new raw value, or null once removed; names the element did not have are appended in this order. */
  readonly changes = new Map<string, string | null>();
  /** A `<Position>` an edit gave an element that had none, written before its first child. */
  position: Element | null = null;
  /** Elements added after the read, written in this order before the end tag. */
  readonly added: Element[] = [];
  /** What a replaced read child writes in its place; null once that was removed too. */
  replacement: Element | null = null;
  /** The children's slots in the order a `move` left them: a read child (standing for its `replacement`) or an added element. */
  order: Element[] | null = null;
  private byTag?: Map<string, Element[]>;

  constructor(readonly source: string, readonly tag: string, readonly start: number, public parent: Element | null) {}

  /** Its children as the edits so far left them: in a move's order once there is one, else the read ones in place (or what replaced them); the added not yet ordered last. */
  current(): Element[] {
    const out: Element[] = this.position && !this.position.removed ? [this.position] : [];
    for (const slot of this.order ?? this.children) {
      const shown = slot.live();
      if (shown) out.push(shown);
    }
    for (const child of this.added) if (!child.removed && !this.order?.includes(child)) out.push(child);
    return out;
  }

  /** Every child slot in written order, removed ones included. */
  slots(): Element[] {
    return this.order ? [...this.order, ...this.added.filter(child => !this.order!.includes(child))] : [...this.children, ...this.added];
  }

  /** What this slot writes: itself, or its replacement once removed; null when neither stands. */
  live(): Element | null {
    const shown = this.removed ? this.replacement : this;
    return shown && !shown.removed ? shown : null;
  }

  attribute(name: string): string | undefined {
    const changed = this.changes.get(name);
    if (changed !== undefined) return changed === null ? undefined : decode(changed);
    return this.original(name);
  }

  original(name: string): string | undefined {
    const found = this.attributes.find(attribute => attribute.name === name);
    return found && decode(this.source.slice(found.valueStart, found.valueEnd));
  }

  childAt(tag: string, index: number): Element | undefined {
    if (!this.byTag) {
      this.byTag = new Map();
      for (const child of this.children) {
        const list = this.byTag.get(child.tag);
        if (list) list.push(child); else this.byTag.set(child.tag, [child]);
      }
    }
    return this.byTag.get(tag)?.[index];
  }

  write(): string {
    if (!this.dirty) return this.source.slice(this.start, this.end);
    if (this.order || this.endTagStart < 0) {
      // The app writes no whitespace between elements (Indent = false, Project.cs), so a reordered app file keeps its bytes.
      const inner = this.current().map(child => child.write()).join('');
      if (this.endTagStart < 0) return inner ? `${this.startTag(true)}${inner}</${this.tag}>` : this.startTag(false);
      return this.startTag(false) + inner + this.source.slice(this.endTagStart, this.end);
    }
    const added = this.added.filter(child => !child.removed).map(child => child.write()).join('');
    let position = this.position && !this.position.removed ? this.position.write() : '';
    let out = this.startTag(false), cursor = this.startTagEnd;
    for (const child of this.children) {
      out += this.source.slice(cursor, child.start) + position;
      position = '';
      const shown = child.live();
      if (shown) out += shown.write();
      cursor = child.end;
    }
    return out + this.source.slice(cursor, this.endTagStart) + position + added + this.source.slice(this.endTagStart, this.end);
  }

  private startTag(open: boolean): string {
    const tail = open ? '>' : this.source.slice(this.attributesEnd, this.startTagEnd);
    if (!this.changes.size) return this.source.slice(this.start, this.attributesEnd) + tail;
    let out = this.source.slice(this.start, this.nameEnd);
    for (const attribute of this.attributes) {
      const value = this.changes.get(attribute.name);
      if (value === undefined) out += this.source.slice(attribute.lead, attribute.valueEnd + 1);
      else if (value !== null) out += this.source.slice(attribute.lead, attribute.valueStart) + value + this.source[attribute.valueEnd];
    }
    for (const [name, value] of this.changes) if (value !== null && !this.attributes.some(attribute => attribute.name === name)) out += ` ${name}="${value}"`;
    return out + tail;
  }
}

const isSpace = (c: string | undefined) => c === ' ' || c === '\t' || c === '\n' || c === '\r';

function skipSpace(text: string, at: number): number {
  while (isSpace(text[at])) at++;
  return at;
}

function nameEnd(text: string, at: number): number {
  while (at < text.length && !isSpace(text[at]) && !'/>=<"\''.includes(text[at])) at++;
  return at;
}

/** `node`'s attributes, from the end of its name to the end of its start tag; true when the tag closes itself. */
function readAttributes(text: string, node: Element, names: Set<string>, fail: (why: string) => never): boolean {
  const tag = node.tag;
  let i = node.nameEnd;
  for (;;) {
    const lead = i;
    i = skipSpace(text, i);
    if (text[i] === '>' || text.startsWith('/>', i)) {
      const closed = text[i] === '/';
      node.attributesEnd = lead;
      node.startTagEnd = i + (closed ? 2 : 1);
      return closed;
    }
    if (i === lead) fail(`<${tag}> has attributes run together or an unclosed start tag`);
    const nameStop = nameEnd(text, i), name = text.slice(i, nameStop);
    let j = skipSpace(text, nameStop);
    if (!name || text[j] !== '=') fail(`a broken attribute on <${tag}>`);
    j = skipSpace(text, j + 1);
    const quote = text[j];
    if (quote !== '"' && quote !== '\'') fail(`${name} on <${tag}> has no quoted value`);
    const valueEnd = text.indexOf(quote, j + 1), stray = text.indexOf('<', j + 1);
    if (valueEnd < 0 || (stray >= 0 && stray < valueEnd)) fail(`the value of ${name} on <${tag}> is not closed`);
    if (node.attributes.some(attribute => attribute.name === name)) fail(`${name} twice on <${tag}>`);
    node.attributes.push({ name, lead, valueStart: j + 1, valueEnd });
    names.add(`${tag}@${name}`);
    i = valueEnd + 1;
  }
}

/** A well-formed document's root element and every name in it (`Element`, `Element@Attribute`); a fragment allows nothing but whitespace around its element. */
function scan(text: string, fragment: boolean): { root: Element | undefined; names: Set<string> } {
  const stack: Element[] = [], names = new Set<string>();
  let root: Element | undefined, at = 0;
  const fail = (why: string): never => { throw new ProjectFileError('malformed', `${why} at ${at}`); };
  const past = (close: string, from: number, what: string) => {
    const found = text.indexOf(close, from);
    return found < 0 ? fail(`an unclosed ${what}`) : found + close.length;
  };
  const forbidden = FORBIDDEN.exec(text);
  if (forbidden) { at = forbidden.index; fail('a character XML forbids'); }
  // Ranges are checked in document order, so one cursor over the `&`s keeps a large file linear.
  let amp = text.indexOf('&');
  const references = (from: number, to: number, where: string) => {
    while (amp >= 0 && amp < from) amp = text.indexOf('&', amp + 1);
    for (; amp >= 0 && amp < to; amp = text.indexOf('&', amp + 1)) {
      REFERENCE.lastIndex = amp;
      if (!REFERENCE.test(text)) fail(`a raw & or an entity XML does not define ${where}`);
      if (text[amp + 1] !== '#') continue;
      const body = text.slice(amp + 2, REFERENCE.lastIndex - 1);
      if (!isXmlChar(body[0] === 'x' ? parseInt(body.slice(1), 16) : parseInt(body, 10))) fail(`a reference to a character XML forbids ${where}`);
    }
  };
  while (at < text.length) {
    const lt = text.indexOf('<', at), textEnd = lt < 0 ? text.length : lt;
    if (!stack.length) {
      for (let i = at; i < textEnd; i++) if (!isSpace(text[i]) && !(i === 0 && !fragment && text[i] === '\uFEFF')) fail('text outside the root element');
    } else references(at, textEnd, `in the text of <${stack[stack.length - 1].tag}>`);
    if (lt < 0) break;
    at = lt;
    if (text.startsWith('<!--', at) || text.startsWith('<?', at)) {
      if (fragment && !stack.length) fail('markup around the element');
      at = text[at + 1] === '?' ? past('?>', at + 2, 'instruction') : past('-->', at + 4, 'comment');
    } else if (text.startsWith('<![CDATA[', at)) {
      if (!stack.length) fail('CDATA outside the root element');
      at = past(']]>', at + 9, 'CDATA section');
    } else if (text[at + 1] === '!') {
      fail('a declaration the app never writes');
    } else if (text[at + 1] === '/') {
      const end = nameEnd(text, at + 2), tag = text.slice(at + 2, end), close = skipSpace(text, end);
      if (text[close] !== '>') fail('a broken end tag');
      const open = stack.pop();
      if (open?.tag !== tag) fail(`</${tag}> closes ${open ? `<${open.tag}>` : 'nothing'}`);
      open!.endTagStart = at;
      open!.end = at = close + 1;
    } else {
      const end = nameEnd(text, at + 1), tag = text.slice(at + 1, end);
      if (!tag) fail('a tag without a name');
      if (root && !stack.length) fail('a second root element');
      const parent = stack[stack.length - 1] ?? null, node = new Element(text, tag, at, parent);
      names.add(tag);
      node.nameEnd = end;
      const closed = readAttributes(text, node, names, fail);
      for (const attribute of node.attributes) references(attribute.valueStart, attribute.valueEnd, `in ${attribute.name} on <${tag}>`);
      if (parent) parent.children.push(node); else root = node;
      if (closed) node.end = node.startTagEnd; else stack.push(node);
      at = node.startTagEnd;
    }
  }
  if (stack.length) fail(`<${stack[stack.length - 1].tag}> is not closed`);
  return { root, names };
}

/** The app derives these from their wall side (`<side id>:plinth`), so a new one needs none. */
const DERIVED_IDS: ReadonlySet<string> = new Set(['Plinth', 'CeiliningMolding']);

function withoutId(node: Element): Element | undefined {
  if (ID_TAGS.has(node.tag) && !DERIVED_IDS.has(node.tag) && !node.attribute('UniqueId')) return node;
  for (const child of node.current()) {
    const found = withoutId(child);
    if (found) return found;
  }
  return undefined;
}

function idsIn(node: Element, ids: string[] = []): string[] {
  const id = node.attribute('UniqueId');
  if (id) ids.push(id);
  for (const child of node.current()) idsIn(child, ids);
  return ids;
}

const sameFloat = (text: string, value: number) => text.trim() !== '' && Math.fround(Number(text)) === value;

export class ProjectFile {
  /** Every holder of each UniqueId in document order, then the added; a removed one stays listed and is skipped. */
  private readonly holders = new Map<string, Element[]>();

  private constructor(private readonly source: string, private readonly root: Element, private readonly names: Set<string>) {
    this.index(root);
  }

  /** The file as read; refused unless it is a well-formed `<Project>`. */
  static read(text: string): ProjectFile {
    const { root, names } = scan(text, false);
    if (root?.tag !== 'Project') throw new ProjectFileError('not-a-project', root ? `the root is <${root.tag}>, not <Project>` : 'no root element');
    return new ProjectFile(text, root, names);
  }

  /** The first element in the file now carrying `id`, read or added; nothing once it was removed. */
  byId(id: string): ProjectElement | undefined {
    return this.holders.get(id)?.find(node => this.attached(node));
  }

  /** Whether some part the app ids has no `UniqueId` (a project saved before ids): the app then derives ids the web cannot see. */
  get idless(): boolean {
    return !!withoutId(this.root);
  }

  parent(element: ProjectElement): ProjectElement | null {
    return this.own(element).parent;
  }

  /** The element's `tag` children as the edits so far left them, added ones last. */
  children(element: ProjectElement, tag: string): ProjectElement[] {
    return this.own(element).current().filter(child => child.tag === tag);
  }

  /** The element at `path` in the file as read: the address stays the element's after other edits, a removed one included. */
  element(path: ElementPath): ProjectElement {
    let node = this.root;
    for (const [tag, index] of path) {
      const next = node.childAt(tag, index);
      if (!next) throw new ProjectFileError('not-found', `no ${path.map(([t, i]) => `${t}[${i}]`).join('/')} in the file`);
      node = next;
    }
    return node;
  }

  /** The element's `<Position X Y>`; at (0, 0) the Position goes, as the app skips a zero point. */
  setPosition(element: ProjectElement, x: number, y: number): void {
    const node = this.own(element), fx = Math.fround(x), fy = Math.fround(y);
    const position = node.current().find(child => child.tag === 'Position');
    if (!position && !PLACED.has(node.tag)) throw new ProjectFileError('unsupported', `a <${node.tag}> has no Position of its own`);
    if (!Number.isFinite(fx) || !Number.isFinite(fy)) throw new ProjectFileError('not-finite', `(${x}, ${y}) is not a point the app can hold`);
    if (fx === 0 && fy === 0) {
      if (position) { position.removed = true; this.touch(node); }
      return;
    }
    if (position) {
      this.number(position, 'X', fx);
      this.number(position, 'Y', fy);
      return;
    }
    for (const name of ['Position', 'Position@X', 'Position@Y']) this.allow(name);
    node.position = scan(`<Position X="${formatFloat(fx)}" Y="${formatFloat(fy)}" />`, true).root!;
    node.position.parent = node;
    this.touch(node);
  }

  /** The element's `Rotation` in degrees; at 0 the attribute goes, as the app skips a zero. */
  setRotation(element: ProjectElement, degrees: number): void {
    const node = this.placed(element), f = Math.fround(degrees);
    if (!Number.isFinite(f)) throw new ProjectFileError('not-finite', `${degrees} is not an angle the app can hold`);
    if (f === 0) this.setAttribute(node, 'Rotation', null);
    else this.number(node, 'Rotation', f);
  }

  remove(element: ProjectElement): void {
    const node = this.own(element);
    if (!node.parent) throw new ProjectFileError('unsupported', 'the <Project> root cannot be removed');
    node.removed = true;
    this.touch(node.parent);
  }

  /**
   * The element to the `index`-th place among its same-tag siblings, as the app's `RemoveAt` + `Insert` leaves it
   * (`DomainChangeReceiver.ApplyLevelOrder`); false when it stands there already.
   */
  move(element: ProjectElement, index: number): boolean {
    const node = this.own(element), parent = node.parent;
    if (!parent) throw new ProjectFileError('unsupported', 'the <Project> root has no siblings');
    const slots = parent.slots(), same = slots.filter(slot => slot.live()?.tag === node.tag);
    if (!Number.isInteger(index) || index < 0 || index >= same.length) throw new ProjectFileError('not-found', `no place ${index} among ${same.length} <${node.tag}>`);
    const from = same.findIndex(slot => slot.live() === node);
    if (from === index) return false;
    const slot = same[from], rest = same.filter(other => other !== slot);
    slots.splice(slots.indexOf(slot), 1);
    slots.splice(index < rest.length ? slots.indexOf(rest[index]) : slots.indexOf(rest[rest.length - 1]) + 1, 0, slot);
    parent.order = slots;
    this.touch(parent);
    return true;
  }

  /** `xml`, one element, as the parent's last child; each part of it the app ids carries a `UniqueId` the file does not hold. */
  insert(parent: ProjectElement, xml: string): ProjectElement {
    const node = this.own(parent), { root, names } = scan(xml, true);
    if (!root) throw new ProjectFileError('malformed', 'no element to add');
    for (const name of names) this.allow(name);
    const missing = withoutId(root);
    if (missing) throw new ProjectFileError('missing-id', `a new <${missing.tag}> needs a UniqueId of its own`);
    const ids = idsIn(root);
    for (const [index, id] of ids.entries()) {
      if (this.byId(id) || ids.indexOf(id) !== index) throw new ProjectFileError('duplicate-id', `UniqueId ${id} is already in the file`);
    }
    this.attach(node, root);
    return root;
  }

  /** The app's add as the last child of the parent's `containerTag` (made when missing), names and parts as written; a held id is a no-op, as in the app. */
  insertFromApp(parent: ProjectElement, containerTag: string, xml: string, uniqueId: string): ProjectElement | null {
    const host = this.own(parent);
    if (this.byId(uniqueId)) return null;
    const root = this.fromApp(xml);
    if (!root.attribute('UniqueId')) root.changes.set('UniqueId', escapeAttribute(uniqueId));
    let container = host.current().find(child => child.tag === containerTag);
    if (!container) this.attach(host, container = this.fromApp(`<${containerTag} />`));
    this.attach(container, root);
    return root;
  }

  /** A number in the app's format; an attribute that reads as the same float keeps its bytes. */
  setNumber(element: ProjectElement, name: string, value: number): void {
    this.number(this.own(element), name, value, true);
  }

  /** Text escaped as .NET writes it, refused when XML forbids a character of it; null removes the attribute. */
  setText(element: ProjectElement, name: string, value: string | null): void {
    const node = this.own(element);
    if (value !== null && FORBIDDEN.test(value)) throw new ProjectFileError('malformed', `${name} holds a character XML forbids`);
    this.setAttribute(node, name, value === null ? null : node.original(name) === value ? undefined : escapeAttribute(value), true);
  }

  /** The element's first `tag` child swapped in place for `xml`, or added last when it had none; null removes it. */
  replaceChild(element: ProjectElement, tag: string, xml: string | null): void {
    const node = this.own(element), next = xml === null ? null : this.fromApp(xml);
    if (next && next.tag !== tag) throw new ProjectFileError('unsupported', `a <${next.tag}> cannot stand for a <${tag}>`);
    const old = node.current().find(child => child.tag === tag);
    if (!old) {
      if (next) this.attach(node, next);
      return;
    }
    old.removed = true;
    const slot = node.children.find(child => child === old || child.replacement === old);
    if (slot) slot.replacement = next;
    else if (next) {
      node.added[node.added.indexOf(old)] = next;
      const at = node.order?.indexOf(old) ?? -1;
      if (at >= 0) node.order![at] = next;
    }
    if (!next) { this.touch(node); return; }
    next.parent = node;
    this.index(next);
    this.touch(next);
  }

  /** The element as the file now holds it: its slice of `write()`. */
  xmlOf(element: ProjectElement): string {
    return this.own(element).write();
  }

  write(): string {
    return this.root.dirty ? this.source.slice(0, this.root.start) + this.root.write() + this.source.slice(this.root.end) : this.source;
  }

  private fromApp(xml: string): Element {
    const { root, names } = scan(xml, true);
    if (!root) throw new ProjectFileError('malformed', 'no element to add');
    for (const name of names) this.names.add(name);
    return root;
  }

  private attach(host: Element, root: Element): void {
    root.parent = host;
    host.added.push(root);
    this.index(root);
    this.touch(root);
  }

  private index(node: Element): void {
    const id = node.attribute('UniqueId');
    if (id) {
      const list = this.holders.get(id);
      if (list) list.push(node); else this.holders.set(id, [node]);
    }
    for (const child of node.current()) this.index(child);
  }

  private attached(node: Element): boolean {
    for (let at: Element | null = node; at; at = at.parent) {
      if (at.removed) return false;
      if (at === this.root) return true;
    }
    return false;
  }

  private own(element: ProjectElement): Element {
    if (!(element instanceof Element)) throw new ProjectFileError('unsupported', 'not an element of a project file');
    let node: Element = element, removed = false;
    while (node.parent) { removed ||= node.removed; node = node.parent; }
    if (node !== this.root) throw new ProjectFileError('unsupported', 'an element of another file');
    if (removed) throw new ProjectFileError('removed', `this <${element.tag}> was removed`);
    return element;
  }

  private placed(element: ProjectElement): Element {
    const node = this.own(element);
    if (!PLACED.has(node.tag)) throw new ProjectFileError('unsupported', `a <${node.tag}> has no Position or Rotation of its own`);
    return node;
  }

  private allow(name: string): void {
    if (!this.names.has(name) && !APP_WRITES.has(name)) throw new ProjectFileError('unknown-name', `${name} is neither in the file nor written by the app`);
  }

  private number(node: Element, name: string, value: number, fromApp = false): void {
    const original = node.original(name), f = Math.fround(value);
    this.setAttribute(node, name, original !== undefined && sameFloat(original, f) ? undefined : formatFloat(f), fromApp);
  }

  /** `undefined` puts back the value as read, `null` removes the attribute. */
  private setAttribute(node: Element, name: string, value: string | null | undefined, fromApp = false): void {
    const had = node.attributes.some(attribute => attribute.name === name);
    if (value === undefined || (value === null && !had)) {
      if (!node.changes.delete(name)) return;
    } else {
      if (value !== null && !had) {
        if (fromApp) this.names.add(`${node.tag}@${name}`); else this.allow(`${node.tag}@${name}`);
      }
      node.changes.set(name, value);
    }
    this.touch(node);
  }

  private touch(node: Element | null): void {
    while (node && !node.dirty) { node.dirty = true; node = node.parent; }
  }
}
